/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ImportResultsPanel — loading a timing company's file into one race.
 *
 * ON THE ORGANIZER SCREEN, NOT THE PUBLIC BOARD. Attaching one run belongs on
 * the board, where the organizer is already looking at the race. A results file
 * does not: it is a staged operation with columns to confirm, rejected lines to
 * read and a re-run after the file is fixed, and it happens BEFORE the board is
 * worth looking at. The board is also a public URL, and an upload control there
 * would make every visitor's render pay for an organizer check.
 *
 * NOT GATED ON "IS THIS RACE BIB-TIMED". Nothing declares that, by design:
 * event_timing_source records where each RESULT came from, after the fact, and
 * no column on event_activity says what a race is going to be. Gating the
 * upload on such a flag would also be circular — uploading the file is the
 * thing that makes a race bib-timed. So the panel is here for every race and
 * the file itself decides what each row becomes.
 *
 * THE ORGANIZER CONFIRMS THE COLUMNS. Every timing company names its columns
 * differently. We guess from the header row, but a guess that silently reads
 * GUN time as the official time is worse than no import at all, so the guess is
 * shown as dropdowns over real rows of the file and nothing is written until it
 * has been looked at.
 */

import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IMPORTABLE_COLUMNS, previewCsv, parseCsv, guessMapping, fetchEntrants, bibIndex,
  buildOperations, saveInChunks, DAILY_IMPORT_BLOCKED,
} from '../feed/results/importApi';
import { isMultiDayEvent } from '../feed/results/dailyMode';
import { fetchTimingSources, timingSourceIdByCode } from '../feed/results/resultsApi';
import { fetchAllRaceResults } from '../feed/results/boardApi';
import { idOf } from '../feed/results/boardIndexModel';
import ChronoBusy from '../feed/results/ChronoBusy';

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const HAIR = 'rgba(36, 23, 8, .12)';
const WARM = '#FFF8F1';
const BAD = '#B42318';

const ORGANIZER_IMPORT = 'ORGANIZER_IMPORT';
const APPLICATION_BIB_TIMED = 'APPLICATION_BIB_TIMED';

const cell = {
  padding: '7px 10px', fontSize: 12, color: MUTED, textAlign: 'left',
  borderBottom: `1px solid ${HAIR}`, whiteSpace: 'nowrap',
  overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170,
};

// `existingResults` is deliberately no longer read: it carries the page's
// capped saved-query rows, and trusting them is what broke the import on any
// race with more than 500 results. The prop stays on the signature so the
// caller is unchanged, and so that anyone reaching for it finds this note and
// the fuller explanation at the read inside onImport.
/**
 * A multi-day event attaches its runs day by day, so it gets an explanation
 * instead of the file picker — see DAILY_IMPORT_BLOCKED for why.
 */
export default function ImportResultsPanel(props) {
  if (isMultiDayEvent(props.eventStart, props.eventEnd, props.eventTimezone)) {
    return (
      <section aria-label="Import results" style={{
        margin: '0 0 22px', padding: '14px 16px', borderRadius: 14, background: WARM,
        border: `1px solid ${HAIR}`, color: INK, fontSize: 14,
      }}>
        <p style={{ margin: 0 }}>{DAILY_IMPORT_BLOCKED}</p>
      </section>
    );
  }
  return <ImportResultsForm {...props} />;
}

// eslint-disable-next-line no-unused-vars
function ImportResultsForm({ api, eventActivityId, existingResults, onImported }) {
  const fileRef = useRef(null);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const sources = useQuery({
    queryKey: ['timing-sources'],
    queryFn: ({ signal }) => fetchTimingSources(api, { signal }),
  });

  function onPick(event) {
    const picked = event.target.files?.[0];
    setError(''); setOutcome(null); setProgress(null);
    if (!picked) { setText(''); setFileName(''); setPreview(null); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const read = String(reader.result || '');
      const seen = previewCsv(read);
      if (!seen.headers.length) { setError('That file has no header row.'); return; }
      if (!seen.count) { setError('That file has a header row but no results in it.'); return; }
      setText(read); setFileName(picked.name); setPreview(seen);
      setMapping(guessMapping(seen.headers));
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsText(picked);
  }

  function setColumnFor(header, column) {
    setMapping((was) => {
      const next = { ...was };
      // One of our columns can only be fed once, so choosing it here takes it
      // away from wherever it was rather than quietly feeding it twice.
      Object.keys(next).forEach((key) => {
        if (next[key] === column && key !== header) delete next[key];
      });
      if (!column) delete next[header]; else next[header] = column;
      return next;
    });
  }

  async function onImport() {
    setError(''); setOutcome(null);
    setProgress({ written: 0, total: 0, chunk: 0, chunks: 0 });
    try {
      const timing = sources.data || [];
      const entrants = await fetchEntrants(api, { eventActivityId });
      const { byBib, duplicates } = bibIndex(entrants);

      if (byBib.size === 0) {
        throw new Error(
          `None of this race's ${entrants.length} entrants has a bib number yet, so no row `
          + 'in the file can be matched to anybody. Bibs have to be assigned first.',
        );
      }

      // WHICH REGISTRATIONS ALREADY HAVE A RESULT. Read here, in full, rather
      // than taken from the page's `existingResults` prop.
      //
      // That prop comes from the organizer's SAVED QUERY, and the analytics
      // endpoint behind it caps every answer at 500 rows with no paging of any
      // kind. On a race with more than 500 results it therefore reports only
      // some of them, every one it misses is planned as a CREATE, and the first
      // batch containing one is refused on `registration_id`'s unique
      // constraint — taking the whole import down. Measured on a 10,000-entrant
      // race with 1,000 results already attached: 0 of 10,000 rows saved, and
      // re-uploading failed identically because the same 500 came back.
      //
      // The old note here said the generic table endpoint refuses event_result
      // to an organizer. That is true of the AUTHENTICATED table read and is
      // why the saved query exists — but this is the PUBLIC one, carrying the
      // client key and no session, so nothing narrows it. It is the same read
      // the public board uses to show every finisher.
      const existing = await fetchAllRaceResults(api, { eventActivityId });

      // KEYED IN EVERY FORM THE ID MIGHT TAKE. `buildOperations` looks this up
      // with `entrant.id`, which comes from a different endpoint than these
      // rows do, and the fetch API is not consistent about handing ids back as
      // numbers or as strings — a Map tells 5 and "5" apart, so one mismatch
      // would report every entrant as having no result and reproduce exactly
      // the failure this read was added to fix. Registering all three forms
      // costs nothing and cannot miss.
      const existingByRegistration = new Map();
      existing.rows.forEach((row) => {
        const id = idOf(row.registration_id);
        if (id === null || id === undefined) return;
        existingByRegistration.set(id, row);
        existingByRegistration.set(Number(id), row);
        existingByRegistration.set(String(id), row);
      });

      const { headers, rows } = parseCsv(text);
      const { rows: payloads, rejections } = buildOperations({
        rows,
        headers,
        mapping,
        byBib,
        duplicates,
        existingByRegistration,
        eventActivityId,
        timingSourceIds: {
          organizerImport: timingSourceIdByCode(timing, ORGANIZER_IMPORT),
          applicationBibTimed: timingSourceIdByCode(timing, APPLICATION_BIB_TIMED),
        },
      });

      const created = payloads.filter((p) => p.id === undefined).length;
      const updated = payloads.length - created;

      if (payloads.length) {
        await saveInChunks(api, payloads, { onProgress: setProgress });
      }

      setOutcome({ created, updated, rejections });
      setProgress(null);
      if (payloads.length && onImported) onImported();
    } catch (e) {
      setError(e.message || 'The import could not be finished.');
      setProgress(null);
    }
  }

  function reset() {
    setText(''); setFileName(''); setPreview(null); setMapping({});
    setError(''); setOutcome(null); setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  const bibMapped = Object.values(mapping).includes('bib');
  const running = progress !== null;

  return (
    <section
      style={{
        border: `1px solid ${HAIR}`, borderRadius: 14, padding: '18px 20px 20px',
        marginBottom: 22, background: WARM,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: INK }}>
        Import a results file
      </h2>
      <p style={{ margin: '6px 0 14px', fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
        A CSV from your timing company. Rows are matched to entrants by bib number, so a bib
        this race has no entrant for is reported back rather than guessed at. Importing the
        same file twice updates the same results instead of doubling them.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onPick}
        disabled={running}
        style={{ fontSize: 13, color: INK }}
      />

      {error && <p style={{ margin: '12px 0 0', fontSize: 13, color: BAD, lineHeight: 1.6 }}>{error}</p>}

      {preview && (
        <>
          <p style={{ margin: '14px 0 8px', fontSize: 13, color: INK, fontWeight: 600 }}>
            {preview.count} row{preview.count === 1 ? '' : 's'} in {fileName}. Check each column
            before importing — the finish time should be the chip time, not the gun time.
          </p>

          <div style={{ overflowX: 'auto', border: `1px solid ${HAIR}`, borderRadius: 10, background: '#fff' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {preview.headers.map((header) => (
                    <th key={header} style={{ ...cell, color: INK, fontWeight: 700 }}>
                      {header}
                      <br />
                      <select
                        value={mapping[header] || ''}
                        onChange={(e) => setColumnFor(header, e.target.value)}
                        disabled={running}
                        style={{
                          marginTop: 5, fontSize: 11, padding: '3px 4px', maxWidth: 160,
                          border: `1px solid ${HAIR}`, borderRadius: 6, color: MUTED,
                        }}
                      >
                        <option value="">Ignore this column</option>
                        {IMPORTABLE_COLUMNS.map((c) => (
                          <option key={c.column} value={c.column}>{c.label}</option>
                        ))}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    {preview.headers.map((header, j) => (
                      <td key={header} style={cell}>{row[j] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!bibMapped && (
            <p style={{ margin: '10px 0 0', fontSize: 13, color: BAD }}>
              One column has to be the bib number — it is the only thing that says which
              entrant a row belongs to.
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onImport}
              disabled={running || !bibMapped}
              style={{
                minHeight: 42, padding: '0 20px', borderRadius: 999, border: 'none',
                background: bibMapped && !running ? ACCENT : HAIR, color: '#FFF6E9',
                fontSize: 14, fontWeight: 700,
                cursor: running || !bibMapped ? 'default' : 'pointer',
              }}
            >
              {/* The waiting state moved to ChronoBusy, below.
                  Was: {running ? 'Importing…' : 'Import these results'} */}
              Import these results
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={running}
              style={{
                minHeight: 42, padding: '0 16px', borderRadius: 999, background: 'none',
                border: `1px solid ${HAIR}`, color: MUTED, fontSize: 13,
                cursor: running ? 'default' : 'pointer',
              }}
            >
              Choose a different file
            </button>
          </div>
        </>
      )}

      {/* The import drives its own chunks, so unlike the attach it knows
          exactly where it has got to and the bar means something.

          The line this replaces sat BELOW the panel, out of view on a long
          file where the organizer had already scrolled to the mapping table —
          so the one honest progress figure on the screen was the one nobody
          could see. The overlay brings it to the middle of the screen and
          takes the panel out of reach until the write finishes.

          Was:
          {running && progress.chunks > 0 && (
            <p style={{ margin: '14px 0 0', fontSize: 13, color: MUTED }}>
              Saving batch {progress.chunk} of {progress.chunks} — {progress.written} of{' '}
              {progress.total} rows. Leave this page open until it finishes.
            </p>
          )} */}
      <ChronoBusy
        open={running}
        title="Importing the results file"
        note={
          progress && progress.chunks > 0
            ? `Saving batch ${progress.chunk} of ${progress.chunks}. Leave this page open until it finishes.`
            : 'Matching every bib in the file to an entrant in this race.'
        }
        progress={
          progress && progress.total > 0
            ? { done: progress.written, total: progress.total }
            : null
        }
      />


      {outcome && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${HAIR}` }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: outcome.rejections.length ? '#9A3412' : '#15803D' }}>
            {describe(outcome)}
          </p>
          {outcome.rejections.length > 0 && (
            <>
              <p style={{ margin: '10px 0 6px', fontSize: 13, color: INK, fontWeight: 600 }}>
                These rows were not imported:
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: MUTED, lineHeight: 1.7 }}>
                {outcome.rejections.slice(0, 50).map((r) => (
                  <li key={r.line}>Line {r.line}: {r.reason}</li>
                ))}
              </ul>
              {outcome.rejections.length > 50 && (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: MUTED }}>
                  … and {outcome.rejections.length - 50} more.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function describe({ created, updated, rejections }) {
  const parts = [];
  if (created) parts.push(`${created} result${created === 1 ? '' : 's'} added`);
  if (updated) parts.push(`${updated} updated`);
  if (!parts.length) parts.push('Nothing was imported');
  const tail = rejections.length
    ? `, ${rejections.length} not imported.`
    : '.';
  return parts.join(', ') + tail;
}
